/* istanbul ignore file */

import React, { forwardRef, memo, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { TextInput } from 'react-native';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { type InterpreterFrom } from 'xstate';

import { type createLocationSearchMachine } from '../../machine';
import { LocationSearchField } from '../LocationSearchField';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchFieldForSearchType = memo(
  forwardRef<TextInput, LocationSearchFieldForSearchTypeProps>((props, ref) => {
    const {
      locationSearchService,
      searchString,
      deliverySearchString,
      isLoadingLocations,
      locationSearchType,
      onClear,
      onDeliverySearchStringChange,
    } = props;

    const { formatMessage } = useIntl();

    // ─── Helpers ─────────────────────────────────────────────────────────

    const onChangeText = useCallback(
      (enteredSearchString: string) => {
        locationSearchService.send({
          type: 'SEARCH_FOR_PREDICTIONS',
          searchString: enteredSearchString,
        });
      },
      [locationSearchService],
    );

    // ─── Delivery ────────────────────────────────────────────────────────

    if (locationSearchType === 'delivery') {
      return (
        <View style={styles.container}>
          <LocationSearchField
            ref={ref}
            style={styles.field}
            value={deliverySearchString}
            isDisabled={isLoadingLocations}
            onChangeText={onDeliverySearchStringChange}
            placeholder={formatMessage(messages.locationDeliverySearchPlaceholder)}
          />
        </View>
      );
    }

    // ─── Pickup | Outpost ────────────────────────────────────────────────

    return (
      <View style={styles.container}>
        <LocationSearchField
          ref={ref}
          style={styles.field}
          value={searchString}
          isDisabled={isLoadingLocations}
          onChangeText={onChangeText}
          enterKeyHint="done"
          dataDetectorTypes="address"
          onClear={onClear}
          placeholder={formatMessage(messages.locationSearchPlaceholder)}
        />
      </View>
    );
  }),
);

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationDeliverySearchPlaceholder: {
    defaultMessage: 'Enter delivery address',
    description: 'Location | Delivery Search Placeholder',
  },
  locationSearchPlaceholder: {
    defaultMessage: 'Search store, city, state, or zip',
    description: 'Location | Search Placeholder',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
  },
  field: {
    minHeight: 42,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchFieldForSearchTypeProps = Readonly<{
  locationSearchService: InterpreterFrom<ReturnType<typeof createLocationSearchMachine>>; // prettier-ignore
  searchString: string;
  deliverySearchString: string;
  locationSearchType?: string;
  isLoadingLocations: boolean;
  onClear: () => void;
  onDeliverySearchStringChange: (searchString: string) => void;
}>;
